import { CalculatorLink } from '@/components/blog/CalculatorLink'
import { BlogLink } from '@/components/blog/BlogLink'

export default function Article() {
  return (
    <>
      <p>
        W 2026 roku, po pierwszej obniżce stóp NBP do 3,75%, wiele osób zastanawia się: lepiej
        wybrać stałe oprocentowanie i zabezpieczyć się przed podwyżkami, czy postawić na zmienne
        i liczyć na dalsze obniżki? To jedna z najważniejszych decyzji przy zaciąganiu kredytu —
        różnica w całkowitym koszcie może sięgać kilkudziesięciu tysięcy złotych.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Czym różnią się oba rodzaje?</h2>
      <p>
        <strong>Zmienne oprocentowanie</strong> składa się z <BlogLink slug="co-to-jest-wibor">WIBOR</BlogLink>
        {' '}i marży banku. Zmienia się co 3 lub 6 miesięcy (zależnie od typu WIBORu w umowie),
        podążając za decyzjami Rady Polityki Pieniężnej.
      </p>
      <p className="mt-3">
        <strong>Stałe oprocentowanie</strong> w Polsce to właściwie "okresowo stałe" — bank
        ustala stałą stopę procentową na 5-7 lat (czasem 10). Po tym okresie rata przelicza
        się na nową stawkę stałą lub zmienną.
      </p>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Aktualna sytuacja (kwiecień 2026):</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Stopa NBP: <strong>3,75%</strong> (po obniżce w marcu 2026)</li>
          <li>WIBOR 6M: <strong>~4,1%</strong></li>
          <li>Oprocentowanie zmienne: WIBOR + marża ~2% = <strong>~6,1%</strong></li>
          <li>Oprocentowanie stałe 5-letnie: <strong>~7,0-7,5%</strong></li>
        </ul>
      </div>

      <p className="mt-3">
        Zwróć uwagę — obecnie stałe oprocentowanie jest o 1-1,5 p.p. wyższe niż zmienne.
        To premia, którą płacisz za przewidywalność.
      </p>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} wiborRate={4.1} bankMargin={2.0} section="interest-rate">
          Sprawdź ratę przy oprocentowaniu zmiennym
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kiedy wybrać stałe oprocentowanie?</h2>
      <p>Stałe oprocentowanie warto rozważyć, gdy:</p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Rata mocno obciąża budżet</strong> — gdy <BlogLink slug="jak-obliczyc-zdolnosc-kredytowa">rata przekracza 35% dochodu</BlogLink>, nawet mały wzrost oprocentowania może być dotkliwy</li>
        <li><strong>Nie masz poduszki finansowej</strong> — brak oszczędności na 3-6 miesięcy wydatków oznacza, że nie wytrzymasz nagłego wzrostu raty</li>
        <li><strong>Cenisz sobie przewidywalność</strong> — stała rata pozwala spokojnie planować budżet, co jest szczególnie ważne przy długim okresie spłaty</li>
        <li><strong>Spodziewasz się wzrostu stóp</strong> — jeśli obawiasz się powrotu inflacji i kolejnych podwyżek, stałe oprocentowanie chroni Cię przez 5-7 lat</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kiedy wybrać zmienne oprocentowanie?</h2>
      <p>Zmienne oprocentowanie może być lepszym wyborem, gdy:</p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Masz stabilne, wysokie dochody</strong> — komfortowo udźwigniesz wzrost raty o 20-30%</li>
        <li><strong>Masz oszczędności</strong> — bufor finansowy pozwala spać spokojnie mimo wahań</li>
        <li><strong>Liczysz na dalsze <BlogLink slug="obnizki-stop-procentowych-2026">obniżki stóp procentowych</BlogLink></strong> — w 2026 roku prognozy wskazują na dalsze obniżki do ok. 3,5%</li>
        <li><strong>Planujesz <BlogLink slug="nadplata-kredytu-hipotecznego">nadpłacać kredyt</BlogLink></strong> — zmienne oprocentowanie jest zwykle niższe, a nadpłaty i tak skracają okres spłaty</li>
        <li><strong>Zamierzasz szybko sprzedać lub refinansować</strong> — jeśli nie planujesz trzymać kredytu 5+ lat, stała stopa nie zdąży się "obronić"</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Porównanie na konkretnym przykładzie</h2>
      <p>
        Kredyt 500 000 zł, 25 lat, rata równa. Porównajmy trzy scenariusze:
      </p>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Scenariusz 1: Zmienne (6,1% dzisiaj)</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Rata początkowa: ~3 260 zł</li>
          <li>Jeśli stopy spadną do 5% w ciągu 2 lat: rata ~2 927 zł</li>
          <li>Jeśli stopy wzrosną do 7,5% za 3 lata: rata ~3 727 zł</li>
        </ul>
      </div>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">Scenariusz 2: Stałe 5-letnie (7,25%)</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Rata przez 5 lat: <strong>~3 614 zł</strong> (niezależnie od zmian WIBOR)</li>
          <li>Po 5 latach: przeliczenie na nowe warunki rynkowe</li>
          <li>Różnica vs zmienne: +354 zł/mies. przez pierwsze lata</li>
        </ul>
      </div>

      <p className="mt-3">
        W ciągu pierwszych 5 lat dopłacasz <strong>~21 000 zł</strong> za stałe oprocentowanie.
        To "ubezpieczenie" od podwyżek — ma sens, jeśli Twój budżet nie wytrzymałby 500 zł
        wzrostu raty. W innym przypadku zmienne zwykle wygrywa, szczególnie w cyklu obniżek.
      </p>

      <div className="mt-4">
        <CalculatorLink propertyPrice={500000} downPaymentPercent={20} wiborRate={0} bankMargin={7.25} section="interest-rate">
          Symuluj ratę przy stałym oprocentowaniu 7,25%
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Na co uważać?</h2>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Stałe oprocentowanie nie jest naprawdę stałe</strong> — obowiązuje tylko 5-7 lat, potem wracasz do punktu wyjścia</li>
        <li><strong>Premia za stabilność</strong> — banki doliczają zapas na ewentualne wzrosty WIBOR, więc oprocentowanie stałe jest zawsze wyższe (porównuj oferty po <BlogLink slug="rrso-kredyt-hipoteczny">RRSO</BlogLink>, nie po samym oprocentowaniu)</li>
        <li><strong>Możliwość zmiany w trakcie</strong> — niektóre banki pozwalają przejść ze zmiennego na stałe (lub odwrotnie) w trakcie kredytu — sprawdź w umowie</li>
        <li><strong>Koszty zmiany</strong> — przejście między opcjami może wiązać się z prowizją</li>
        <li><strong>Wcześniejsza spłata</strong> — przy stałym oprocentowaniu niektóre banki pobierają opłaty za nadpłatę w pierwszych latach</li>
        <li><strong>WIBOR zostanie zastąpiony przez POLSTR</strong> — do końca 2027 roku wszystkie umowy ze zmiennym oprocentowaniem zostaną automatycznie skonwertowane. Zmiana ma być ekonomicznie neutralna (spread korygujący), ale POLSTR Compound aktualizuje się co miesiąc, a nie co 3-6 miesięcy jak WIBOR — szczegóły w artykule o <BlogLink slug="polstr-zamiast-wibor">POLSTR zamiast WIBOR</BlogLink></li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Decyzja w 5 pytaniach</h2>
      <ol className="list-decimal pl-6 space-y-2 mt-3">
        <li>Czy wytrzymasz wzrost raty o 30-40%? <em>Nie → stałe</em></li>
        <li>Czy masz 6-miesięczną poduszkę finansową? <em>Nie → stałe</em></li>
        <li>Czy planujesz <BlogLink slug="nadplata-kredytu-hipotecznego">aktywnie nadpłacać</BlogLink>? <em>Tak → zmienne</em></li>
        <li>Czy wierzysz w dalsze obniżki stóp? <em>Tak → zmienne</em></li>
        <li>Czy cenisz spokój ważniejszy od potencjalnych oszczędności? <em>Tak → stałe</em></li>
      </ol>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Podsumowanie</h2>
      <p>
        W kwietniu 2026 roku zmienne oprocentowanie jest o 1-1,5 p.p. niższe od stałego, co
        przy kredycie 500k oznacza ~350-500 zł różnicy w miesięcznej racie. Jeśli masz
        stabilne dochody i poduszkę finansową — zmienne zwykle wygrywa, szczególnie w cyklu
        obniżek. Jeśli rata jest na granicy Twoich możliwości lub boisz się nieprzewidywalności —
        stałe oprocentowanie to warta rozważenia "polisa" na 5-7 lat.
      </p>

      <div className="mt-4">
        <CalculatorLink section="interest-rate">
          Porównaj oba warianty w kalkulatorze RatyGo
        </CalculatorLink>
      </div>
    </>
  )
}
